// ═══════════════════════════════════════════════════
//  Tracking Tab — Supply Chain Journey + Shipments
// ═══════════════════════════════════════════════════
import React, { useState } from "react";
import {
  Truck, MapPin, Package2, FileText, Eye, Download,
} from "lucide-react";

import { ScanData } from "./types";
import { formatDateTime, getFileUrl, formatFileSize, isImageFile, isPdfFile } from "./helpers";

interface TrackingTabProps {
  scanData: ScanData;
  shipments: any[];
  loadingShipments: boolean;
}

const TrackingTab: React.FC<TrackingTabProps> = ({
  scanData,
  shipments,
  loadingShipments,
}) => {
  const [selectedFile, setSelectedFile] = useState<any>(null);

  return (
    <div className="space-y-6">
      {/* ═══ SUPPLY CHAIN JOURNEY ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Truck className="w-6 h-6 text-orange-600" />
          <h3 className="text-xl font-bold text-gray-900">Supply Chain Journey</h3>
          {(scanData?.traceability?.batchId || scanData?.product?.batchId) && (
            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
              Batch: {scanData.traceability?.batchId || scanData.product?.batchId}
            </span>
          )}
          {scanData?.traceability?.status && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              scanData.traceability.status === "completed" ? "bg-green-100 text-green-700" :
              scanData.traceability.status === "in_transit" ? "bg-blue-100 text-blue-700" :
              scanData.traceability.status === "recalled" ? "bg-red-100 text-red-700" :
              "bg-gray-100 text-gray-700"
            }`}>
              {scanData.traceability.status}
            </span>
          )}
        </div>

        {/* Activation Details */}
        {scanData?.traceability?.activation && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-green-900 mb-2">Activation Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-green-700">Method:</span>
                <p className="font-medium text-green-900">{scanData.traceability.activation.method || "N/A"}</p>
              </div>
              <div>
                <span className="text-green-700">Location:</span>
                <p className="font-medium text-green-900">
                  {(() => {
                    const loc = scanData.traceability!.activation.location;
                    if (!loc) return "Unknown";
                    const parsed = typeof loc === 'string' ? (() => { try { return JSON.parse(loc); } catch { return loc; } })() : loc;
                    if (typeof parsed === 'string') return parsed;
                    return parsed.city || parsed.address?.formatted || parsed.address
                      || (parsed.latitude ? `${Number(parsed.latitude).toFixed(4)}, ${Number(parsed.longitude).toFixed(4)}` : "Unknown");
                  })()}
                </p>
              </div>
              <div>
                <span className="text-green-700">Activated At:</span>
                <p className="font-medium text-green-900">{formatDateTime(scanData.traceability.activation.activatedAt)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        {scanData?.traceability?.stages?.length ? (
          <div className="relative border-l-4 border-orange-300 pl-6 space-y-6">
            {scanData.traceability.stages.map((stage: any, idx: number) => {
              const colorMap: Record<string, { dot: string; card: string; text: string }> = {
                recalled: { dot: "bg-red-500", card: "bg-red-50 border-red-200", text: "text-red-800" },
                received: { dot: "bg-green-500", card: "bg-green-50 border-green-200", text: "text-green-800" },
                in_transit: { dot: "bg-blue-500", card: "bg-blue-50 border-blue-200", text: "text-blue-800" },
                verified: { dot: "bg-emerald-500", card: "bg-emerald-50 border-emerald-200", text: "text-emerald-800" },
                completed: { dot: "bg-purple-500", card: "bg-purple-50 border-purple-200", text: "text-purple-800" },
                default: { dot: "bg-gray-400", card: "bg-gray-50 border-gray-200", text: "text-gray-800" },
              };
              const c = colorMap[stage.status] || colorMap.default;
              return (
                <div key={idx} className="relative">
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 ${c.dot} rounded-full border-2 border-white shadow`}></div>
                  <div className={`rounded-lg p-4 border ${c.card}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className={`${c.text} font-semibold capitalize text-lg`}>{stage.name || "Unknown Stage"}</span>
                      <span className="text-xs text-gray-500">{formatDateTime(stage.timestamp)}</span>
                    </div>
                    {stage.location && (
                      <div className="space-y-1 mb-2">
                        {stage.location.city && <p className="text-sm text-gray-700">📍 {stage.location.city}{stage.location.facility && ` - ${stage.location.facility}`}</p>}
                        {stage.location.address && <p className="text-xs text-gray-600">{stage.location.address}</p>}
                      </div>
                    )}
                    <div className="space-y-1">
                      {stage.status && <p className="text-xs text-gray-600"><strong>Status:</strong> {stage.status}</p>}
                      {stage.description && <p className="text-xs text-gray-600"><strong>Details:</strong> {stage.description}</p>}
                      {stage.operator && <p className="text-xs text-gray-600"><strong>Handler:</strong> {stage.operator}</p>}
                    </div>
                    {stage.documents && stage.documents.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">📎 Attached Documents:</p>
                        <div className="flex flex-wrap gap-2">
                          {stage.documents.map((doc: any, docIdx: number) => (
                            <button key={docIdx} onClick={() => setSelectedFile(doc)}
                              className="text-xs bg-white border border-gray-300 px-2 py-1 rounded hover:bg-gray-50 flex items-center gap-1">
                              <FileText className="w-3 h-3" /> {doc.description || `Document ${docIdx + 1}`}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <h4 className="text-lg font-semibold text-gray-600 mb-2">No Supply Chain Data</h4>
            <p className="text-sm">Traceability details will appear when available.</p>
          </div>
        )}
      </div>

      {/* ═══ SHIPMENTS ═══ */}
      {loadingShipments ? (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="text-center py-8 text-gray-500">
            <Truck className="w-12 h-12 mx-auto mb-3 text-gray-400 animate-pulse" />
            <p>Loading shipments...</p>
          </div>
        </div>
      ) : shipments.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Package2 className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-900">Shipment Details</h3>
            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
              {shipments.length} Shipment{shipments.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="space-y-4">
            {shipments.map((shipment: any, idx: number) => (
              <div key={shipment.shipmentId || idx} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-bold text-blue-900 text-lg">Shipment #{idx + 1}</h4>
                    <p className="text-sm text-blue-700">ID: {shipment.shipmentId}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    shipment.status === "delivered" ? "bg-green-100 text-green-700" :
                    shipment.status === "in_transit" ? "bg-blue-100 text-blue-700" :
                    "bg-gray-100 text-gray-700"
                  }`}>
                    {shipment.status || "Unknown"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {shipment.carrier && <InfoBox label="Carrier" value={shipment.carrier} />}
                  {shipment.trackingNumber && <InfoBox label="Tracking Number" value={shipment.trackingNumber} mono />}
                  {shipment.origin?.city && <InfoBox label="Origin" value={shipment.origin.city} />}
                  {shipment.destination?.city && <InfoBox label="Destination" value={shipment.destination.city} />}
                  {shipment.shippedAt && <InfoBox label="Shipped" value={formatDateTime(shipment.shippedAt)} />}
                  {shipment.estimatedDelivery && <InfoBox label="Est. Delivery" value={formatDateTime(shipment.estimatedDelivery)} />}
                </div>
                {shipment.files && shipment.files.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <p className="text-sm font-semibold text-blue-900 mb-3">📎 Documents ({shipment.files.length})</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {shipment.files.map((file: any, fileIdx: number) => (
                        <div key={fileIdx} className="bg-white border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start gap-2 mb-2">
                            {isImageFile(file.mimeType) ? (
                              <img src={getFileUrl(file.url)} alt={file.description} className="w-16 h-16 object-cover rounded border" />
                            ) : (
                              <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                                <FileText className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{file.description || file.originalName || `File ${fileIdx + 1}`}</p>
                              <p className="text-xs text-gray-500">{file.type || "document"}</p>
                              {file.size && <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setSelectedFile(file)}
                              className="flex-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs hover:bg-blue-200 flex items-center justify-center gap-1">
                              <Eye className="w-3 h-3" /> View
                            </button>
                            <a href={getFileUrl(file.url)} download
                              className="flex-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs hover:bg-green-200 flex items-center justify-center gap-1">
                              <Download className="w-3 h-3" /> Download
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {shipment.statusHistory && shipment.statusHistory.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <p className="text-sm font-semibold text-blue-900 mb-2">📋 Status History</p>
                    <div className="space-y-2">
                      {shipment.statusHistory.map((history: any, histIdx: number) => (
                        <div key={histIdx} className="bg-white p-2 rounded text-xs flex justify-between items-center">
                          <div>
                            <span className="font-medium text-gray-900">{history.toStatus}</span>
                            {history.reason && <span className="text-gray-600 ml-2">- {history.reason}</span>}
                          </div>
                          <span className="text-gray-500">{formatDateTime(history.changedAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* ═══ FILE PREVIEW MODAL ═══ */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedFile(null)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">{selectedFile.description || selectedFile.originalName || "File Preview"}</h3>
              <button onClick={() => setSelectedFile(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4">
              {isImageFile(selectedFile.mimeType) ? (
                <img src={getFileUrl(selectedFile.url)} alt="Preview" className="w-full h-auto rounded-lg" onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.png"; }} />
              ) : isPdfFile(selectedFile.mimeType) ? (
                <iframe src={getFileUrl(selectedFile.url)} className="w-full h-[600px] rounded-lg border" title="PDF Preview" />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                  <a href={getFileUrl(selectedFile.url)} download className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 inline-flex items-center gap-2">
                    <Download className="w-4 h-4" /> Download File
                  </a>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedFile.mimeType && <span>Type: {selectedFile.mimeType}</span>}
                {selectedFile.size && <span className="ml-4">Size: {formatFileSize(selectedFile.size)}</span>}
              </div>
              <a href={getFileUrl(selectedFile.url)} download className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2">
                <Download className="w-4 h-4" /> Download
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Small info box used in shipment cards */
const InfoBox: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="bg-white p-3 rounded-lg">
    <p className="text-xs text-gray-600 mb-1">{label}</p>
    <p className={`text-sm ${mono ? "font-mono text-blue-600" : "text-gray-900"}`}>{value}</p>
  </div>
);

export default TrackingTab;
